import React, { useState, useEffect } from 'react';
import { networkUtils } from '../utils/networkUtils';

export function NetworkStatusBanner() {
  // IMPORTANT: Use function form of useState to avoid accessing navigator during SSR
  const [isOffline, setIsOffline] = useState(() =>
    typeof navigator !== 'undefined' ? !navigator.onLine : false
  );
  const [showBanner, setShowBanner] = useState(false);
  const [wasOffline, setWasOffline] = useState(false);

  useEffect(() => {
    const handleOnline = () => {
      setIsOffline(false);
      if (wasOffline) {
        setShowBanner(true);
        // Hide the "back online" banner after 5 seconds
        setTimeout(() => setShowBanner(false), 5000);
      }
      setWasOffline(false);
    };

    const handleOffline = () => {
      setIsOffline(true);
      setShowBanner(true);
      setWasOffline(true);
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // Also listen to our custom network utils events
    const unsubscribeReconnect = networkUtils.onReconnect(() => {
      handleOnline();
    });

    // Check initial state (only in browser)
    if (typeof navigator !== 'undefined' && !navigator.onLine) {
      handleOffline();
    }

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      unsubscribeReconnect();
    };
  }, [wasOffline]);

  if (!showBanner) return null;

  return (
    <div className={`fixed top-0 left-0 right-0 z-50 ${
      isOffline 
        ? 'bg-yellow-500 text-yellow-900' 
        : 'bg-green-500 text-green-900'
    } px-4 py-2 text-center text-sm font-medium shadow-lg`}>
      <div className="flex items-center justify-center space-x-2">
        {isOffline ? (
          <>
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 5.636l-3.536 3.536m0 5.656l3.536 3.536M9.172 9.172L5.636 5.636m3.536 9.192L5.636 18.364M12 2.5a9.5 9.5 0 11-9.5 9.5 9.5 9.5 0 019.5-9.5z" />
            </svg>
            <span>You're offline. Some features may not work properly.</span>
          </>
        ) : (
          <>
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
            <span>Back online! Your data will now sync.</span>
          </>
        )}
        {isOffline && (
          <button
            onClick={() => setShowBanner(false)}
            className="ml-4 text-yellow-900 hover:text-yellow-800"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        )}
      </div>
    </div>
  );
}